import type { AxiosRequestConfig } from 'axios'
import { conditionalObjectMerge } from '@/lib/utils'

const MISSING_ORGANIZATION_ID_ERROR =
  'Scoped admin requests and query keys require a valid organizationId.'

/**
 * An organization id as callers hold it: route params arrive as strings, loaders and payloads
 * pass numbers. API params and query keys use positive integers; `requireOrganizationId` checks.
 */
export type OrganizationId = number | string

/** An organization id that may be missing. */
export type OrganizationIdInput = OrganizationId | null | undefined

type QueryKeyEntries = Record<string, unknown>

/** The single object inside every admin query key. */
export type AdminQueryRoot = QueryKeyEntries & { organizationId?: number }

/** A scoped key's root: the scoped builders throw rather than leave the organization out. */
export type ScopedAdminQueryRoot = QueryKeyEntries & { organizationId: number }

export type AdminQueryKey = [AdminQueryRoot]

/**
 * A key from `buildScopedAdminQueryKey`. Its query function can pass the key's `organizationId`
 * straight to `buildAdminRequestConfig`.
 */
export type ScopedAdminQueryKey = [ScopedAdminQueryRoot]

interface OrganizationScopeOptions {
  organizationId?: OrganizationIdInput
  scoped?: boolean
}

interface AdminRequestConfigBase extends Omit<AxiosRequestConfig, 'params'> {
  params?: Record<string, unknown>
}

/** A tenant request sends `organization_id`, so it needs the organization's id. */
export interface ScopedAdminRequestConfigOptions extends AdminRequestConfigBase {
  organizationId: OrganizationId
  scoped?: true
}

/** A request outside any organization sends no `organization_id`. */
interface UnscopedAdminRequestConfigOptions extends AdminRequestConfigBase {
  organizationId?: never
  scoped: false
}

export type AdminRequestConfigOptions =
  | ScopedAdminRequestConfigOptions
  | UnscopedAdminRequestConfigOptions

export function normalizeOrganizationId(organizationId: OrganizationIdInput): number | undefined {
  if (typeof organizationId === 'undefined' || organizationId === null || organizationId === '') {
    return undefined
  }

  const normalizedOrganizationId = Number(organizationId)

  if (!Number.isInteger(normalizedOrganizationId) || normalizedOrganizationId <= 0) {
    throw new Error(MISSING_ORGANIZATION_ID_ERROR)
  }

  return normalizedOrganizationId
}

export function requireOrganizationId(organizationId: OrganizationIdInput): number {
  const normalizedOrganizationId = normalizeOrganizationId(organizationId)

  if (!normalizedOrganizationId) {
    throw new Error(MISSING_ORGANIZATION_ID_ERROR)
  }

  return normalizedOrganizationId
}

export function resolveOrganizationId(
  organizationId: OrganizationIdInput,
  fallbackOrganizationId?: OrganizationIdInput
): number {
  return requireOrganizationId(organizationId ?? fallbackOrganizationId)
}

function scopedQueryRoot(
  root: QueryKeyEntries,
  organizationId: OrganizationIdInput
): ScopedAdminQueryRoot {
  return Object.assign({ ...root }, { organizationId: requireOrganizationId(organizationId) })
}

/** The root for a key that keeps its other entries in a second object. */
export function buildScopedAdminQueryRoot(
  root: QueryKeyEntries,
  { organizationId }: { organizationId: OrganizationId }
): ScopedAdminQueryRoot {
  return scopedQueryRoot(root, organizationId)
}

export function buildAdminQueryRoot(
  root: QueryKeyEntries,
  { organizationId, scoped = false }: OrganizationScopeOptions = {}
): AdminQueryRoot {
  if (scoped) {
    return scopedQueryRoot(root, organizationId)
  }

  return conditionalObjectMerge(
    { organizationId: normalizeOrganizationId(organizationId) },
    { ...root }
  )
}

export function buildAdminQueryKey(
  root: QueryKeyEntries,
  { organizationId, scoped = false, ...entries }: OrganizationScopeOptions & QueryKeyEntries = {}
): AdminQueryKey {
  return [conditionalObjectMerge(entries, buildAdminQueryRoot(root, { organizationId, scoped }))]
}

/**
 * Key builders may still pass a missing organization, which throws here. The key it returns always
 * carries one, so query functions type their key entries with a required organization.
 */
export function buildScopedAdminQueryKey(
  root: QueryKeyEntries,
  { organizationId, ...entries }: { organizationId?: OrganizationIdInput } & QueryKeyEntries = {}
): ScopedAdminQueryKey {
  return [conditionalObjectMerge(entries, scopedQueryRoot(root, organizationId))]
}

export function buildAdminRequestConfig({
  organizationId,
  params,
  scoped = true,
  ...config
}: AdminRequestConfigOptions): AxiosRequestConfig {
  if (!scoped) {
    return {
      ...config,
      ...(typeof params === 'undefined' ? {} : { params }),
    }
  }

  return {
    ...config,
    params: {
      ...(params ?? {}),
      organization_id: requireOrganizationId(organizationId),
    },
  }
}
